import type { Timestamp } from 'firebase/firestore'

export type StockStatus = 'Available' | 'In-Use' | 'Maintenance'
export type StockConsumptionMode = 'reserve' | 'consume'

export interface StockRequirement {
  stockItemId: string
  quantity: number
  unitOverride?: string | null
  /** If false, maintenance is advisory only. Missing/true means the item is
   * required for completion resource checks. */
  required?: boolean
}

export interface StockItem {
  id: string
  workspaceId: string
  name: string
  sku: string
  /** Server-only: total quantity across all locations maintained strictly by stock ledger movements */
  quantity: number
  /** Server-only: reserved quantity maintained by stock ledger movements */
  reservedQuantity: number
  unit: string
  location: string
  status: StockStatus
  lastUpdated: Timestamp | null
  linkedTaskIds: string[]
  // Part 1A extensions
  category?: string
  tags?: string[]
  costPerUnit?: number | null
  supplierId?: string | null
  minThreshold?: number | null
  maxThreshold?: number | null
  reorderPoint?: number | null
  reorderQuantity?: number | null
  imageUrl?: string | null
  barcode?: string | null
  expiryDate?: Timestamp | null
  serialNumbers?: string[]
  notes?: string
  createdAt?: Timestamp | null
  updatedAt?: Timestamp | null
  createdById?: string | null
  updatedById?: string | null
  /** Server-only: per-location balance breakdown; sum equals quantity */
  byLocation?: Record<string, number>
  /** Server-only: total units currently in transit across transfers */
  inTransit?: number
  quantityStep?: 1 | 0.5 | 0.1 | 0.01
  tracking?: 'NONE' | 'BATCH' | 'SERIAL'
  /** Server-only: id of the last movement applied to this item */
  lastMovementId?: string | null
  /** Server-only: timestamp of the last movement applied to this item */
  lastMovementAt?: Timestamp | null
  /** Optional per-location reorder minimums (FI-D16) */
  reorderPointByLocation?: Record<string, number>
}

export interface StockSupplier {
  id: string
  workspaceId: string
  name: string
  contactEmail?: string | null
  contactPhone?: string | null
  address?: string | null
  notes?: string | null
  createdAt: Timestamp | null
}

export type StockSupplierDraft = Omit<StockSupplier, 'id' | 'createdAt'> & {
  createdAt?: Timestamp | null
}

export type StockItemDraft = Omit<StockItem, 'id' | 'workspaceId' | 'lastUpdated' | 'linkedTaskIds' | 'reservedQuantity' | 'status' | 'expiryDate'> & {
  reservedQuantity?: number
  status?: StockStatus
  expiryDate?: Timestamp | Date | string | null
}

export type StockAuditAction =
  | 'stock.create'
  | 'stock.update'
  | 'stock.delete'
  | 'stock.reserve'
  | 'stock.consume'
  | 'stock.release'
  | 'stock.shortage'
  | 'stock.adjust'
  | 'stock.reorder.request'
  | 'stock.reorder.receive'
  | 'stock.image.upload'
  | 'stock.bulk.import'

export interface StockAuditEvent {
  action: StockAuditAction
  stockItemId: string
  taskId?: string | null
  quantityDelta?: number
  reservedDelta?: number
  actorId?: string | null
  actorName?: string | null
  timestamp?: Timestamp | null
  payload?: Record<string, unknown>
}

export type StockAdjustmentReason = 'count' | 'shrinkage' | 'damaged' | 'received' | 'manual'
export type StockAdjustmentStatus = 'pending_approval' | 'approved' | 'rejected'

export interface StockAdjustment {
  id: string
  workspaceId: string
  stockItemId: string
  stockItemName?: string | null
  delta: number
  reason: StockAdjustmentReason
  note?: string | null
  createdById: string
  createdByName?: string | null
  timestamp: Timestamp | null
  status?: StockAdjustmentStatus
  previousQuantity?: number
  newQuantity?: number
}

export type StockRestockStatus = 'pending' | 'ordered' | 'received' | 'cancelled'

export interface StockRestockRequest {
  id: string
  workspaceId: string
  stockItemId: string
  stockItemName?: string | null
  locationId?: string | null
  requestedById: string
  requestedByName?: string | null
  requestedAt: Timestamp | null
  orderedAt?: Timestamp | null
  orderedById?: string | null
  orderedByName?: string | null
  receivedAt?: Timestamp | null
  receivedById?: string | null
  receivedByName?: string | null
  receivedQuantity?: number | null
  suggestedQuantity?: number | null
  source?: 'AUTO' | 'MANUAL'
  status: StockRestockStatus
  note?: string | null
}

export type StockLocationKind = 'STORE' | 'VEHICLE' | 'SITE' | 'OTHER'

export interface StockLocation {
  id: string
  workspaceId: string
  name: string
  kind: StockLocationKind
  isDefault: boolean
  archived: boolean
  createdAt: Timestamp | null
  createdById: string | null
}

export type StockMovementType =
  | 'IN'
  | 'OUT'
  | 'COUNT'
  | 'ADJUST'
  | 'WRITE_OFF'
  | 'RECEIVE'
  | 'TRANSFER_OUT'
  | 'TRANSFER_IN'
  | 'RESERVE'
  | 'RELEASE'
  | 'CONSUME'

export type StockMovementSourceKind =
  | 'MANUAL'
  | 'SUGGESTION'
  | 'ADJUSTMENT'
  | 'TRANSFER'
  | 'RESTOCK'
  | 'FORM'
  | 'CHECKLIST'
  | 'IMPORT'

export interface StockMovementSource {
  kind: StockMovementSourceKind
  id: string
}

export interface StockMovement {
  id: string
  workspaceId: string
  itemId: string
  type: StockMovementType
  delta: number
  before: number
  after: number
  locationId: string
  lotId?: string | null
  reason?: string | null
  note?: string | null
  source: StockMovementSource
  actorId: string
  actorRole: string
  approvedById?: string | null
  createdAt: Timestamp | null
  undoOf?: string | null
  undoneBy?: string | null
}

export type StockLotKind = 'BATCH' | 'SERIAL'
export type StockLotStatus = 'ACTIVE' | 'CONSUMED' | 'WRITTEN_OFF'

export interface StockLot {
  id: string
  workspaceId?: string
  itemId?: string
  kind: StockLotKind
  code: string
  expiryDate?: Timestamp | null
  quantity: number
  locationId: string
  status: StockLotStatus
}

export interface StockTransferLine {
  itemId: string
  quantity: number
  lotId?: string | null
  lotCode?: string | null
  expiryDate?: Timestamp | null
  serials?: string[] | null
}

export type StockTransferStatus = 'IN_TRANSIT' | 'RECEIVED' | 'CANCELLED'

export interface StockTransfer {
  id: string
  workspaceId: string
  fromLocationId: string
  toLocationId: string
  lines: StockTransferLine[]
  status: StockTransferStatus
  sentById: string
  sentAt: Timestamp | null
  receivedById?: string | null
  receivedAt?: Timestamp | null
  cancelledById?: string | null
  cancelledAt?: Timestamp | null
  note?: string | null
  cancelNote?: string | null
  requestId?: string | null
}

export const STOCK_COLLECTION = 'workspace_stock'
export const STOCK_ADJUSTMENTS_COLLECTION = 'stock_adjustments'
export const STOCK_RESTOCK_REQUESTS_COLLECTION = 'stock_restock_requests'
export const STOCK_LOCATIONS_COLLECTION = 'workspace_stock_locations'
export const STOCK_MOVEMENTS_COLLECTION = 'workspace_stock_movements'
export const STOCK_TRANSFERS_COLLECTION = 'stock_transfers'

export const deriveStockStatus = (
  quantity: number,
  reservedQuantity: number,
  maintenance: boolean,
): StockStatus => {
  if (maintenance) return 'Maintenance'
  if (reservedQuantity >= quantity && quantity > 0) return 'In-Use'
  return 'Available'
}
